import type { AudioEngine } from "../AudioEngine";
import type { Composer } from "../composer/Composer";
import type { BarContext } from "../composer/types";

// Atmospheric pad. Three slightly detuned saws -> wide lowpass ->
// always-on chord. Smoothly retunes to whatever chord the Composer
// publishes on each bar so the pad walks the progression instead of
// holding one chord forever.
//
// Voice budget is fixed at 3: not every chord has 3 voices in its
// resolved notesHz[] (sus2/sus4 are triads, power is a dyad). When the
// chord has fewer notes we double the root in the missing slot so the
// graph never has to be torn down.
const DETUNE_CENTS = 6;
const FILTER_HZ = 1400;

export class PadSynth {
  private engine: AudioEngine;
  private composer: Composer | null = null;

  private oscs: OscillatorNode[] = [];
  private filter: BiquadFilterNode | null = null;
  private level: GainNode | null = null;
  private playing = false;
  // Last applied chord voicing (in Hz) so we can adopt it after start().
  private currentVoices: number[] = [220, 277, 330];
  private masterGainValue = 0;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  bindComposer(composer: Composer): void {
    if (this.composer) {
      // No way to unsubscribe directly; we just rebind to the new one.
    }
    this.composer = composer;
    composer.onBar.on((ctx) => this.onBar(ctx));
    const cur = composer.getCurrentContext();
    if (cur) this.adoptChord(cur);
  }

  start(): void {
    if (this.playing) return;
    const ctx = this.engine.context;
    const dest = this.engine.musicDestination ?? this.engine.destination;
    if (!ctx || !dest) return;

    this.filter = ctx.createBiquadFilter();
    this.filter.type = "lowpass";
    this.filter.frequency.value = FILTER_HZ;
    this.filter.Q.value = 0.6;

    this.level = ctx.createGain();
    this.level.gain.value = this.masterGainValue;
    this.filter.connect(this.level).connect(dest);

    this.oscs = [];
    const detunes = [-DETUNE_CENTS, 0, DETUNE_CENTS];
    for (let i = 0; i < 3; i++) {
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = this.currentVoices[i] ?? this.currentVoices[0]!;
      o.detune.value = detunes[i] ?? 0;
      const voiceGain = ctx.createGain();
      voiceGain.gain.value = 0.18;
      o.connect(voiceGain).connect(this.filter);
      o.start();
      this.oscs.push(o);
    }
    this.playing = true;
  }

  stop(): void {
    if (!this.playing) return;
    const t = this.engine.context?.currentTime ?? 0;
    for (const o of this.oscs) {
      try { o.stop(t + 0.1); } catch { /* not started */ }
      try { o.disconnect(); } catch { /* gone */ }
    }
    this.oscs.length = 0;
    if (this.filter) try { this.filter.disconnect(); } catch { /* gone */ }
    if (this.level) try { this.level.disconnect(); } catch { /* gone */ }
    this.filter = null;
    this.level = null;
    this.composer = null;
    this.playing = false;
  }

  // Manual passthrough kept for callers that don't drive via Composer.
  setPitch(rootHz: number): void {
    let hz = rootHz;
    while (hz < 130) hz *= 2;
    while (hz > 320) hz *= 0.5;
    this.currentVoices = [hz, hz * 1.2, hz * 1.5];
    this.applyVoices();
  }

  setLevel(gain01: number, rampMs = 1600): void {
    this.masterGainValue = Math.max(0, Math.min(1, gain01));
    if (!this.level || !this.engine.context) return;
    const t = this.engine.context.currentTime;
    const g = this.level.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(this.masterGainValue, t + rampMs / 1000);
  }

  private onBar(ctx: BarContext): void {
    this.adoptChord(ctx);
  }

  private adoptChord(ctx: BarContext): void {
    // Use first 3 chord voices in mid register. Repeat root if the
    // chord has fewer than 3 voices (e.g. power chord).
    const notes = ctx.chord.notesHz.slice(0, 3);
    while (notes.length < 3) notes.push(ctx.chord.rootHz);
    this.currentVoices = notes;
    this.applyVoices();
  }

  private applyVoices(): void {
    if (!this.engine.context || this.oscs.length !== 3) return;
    const t = this.engine.context.currentTime;
    for (let i = 0; i < this.oscs.length; i++) {
      const f = this.oscs[i]!.frequency;
      f.cancelScheduledValues(t);
      f.setTargetAtTime(this.currentVoices[i]!, t, 0.12);
    }
  }
}
